import { PhoneNumberField } from '@ticketmaster/tm1pos-web-shared/components/phone-number-field/phone-number-field';
import { parsePhoneNumber } from '@ticketmaster/tm1pos-web-shared/components/phone-number-field/phone-number-utils';
import { validateEmail } from '@ticketmaster/tm1pos-web-shared/utils/emailValidation';
import { validatePhoneNumber } from '@ticketmaster/tm1pos-web-shared/utils/phoneValidation';
import { Tm1TextArea, Tm1TextField } from '@tm1/design-system-react';
import { useEffect, useState } from 'react';
import { injectIntl } from 'react-intl';
import { useDispatch } from 'react-redux';
import { change } from 'redux-form';
import { useCheckoutContext } from '../../../../../../../../context/checkout-provider';
import { ARCHTICS_EVENT_TYPE, ARCHTICS_NOTE_LIMITATION, HOST_NOTE_LIMITATION } from '../../../../../../../constants';
import messages from '../../../../../messages';
import { isDeliveryFormValid } from '../../../checkout-utils';
import { CHECKOUT_FORM_TITLE } from '../../../constants';
import type { PhoneNumber } from '@ticketmaster/tm1pos-web-shared/components/phone-number-field/phone-number-field';
import type { CountryCode } from 'libphonenumber-js';
import type React from 'react';
import type { InjectedIntlProps } from 'react-intl';

import './text-sms-delivery-form.scss';

export type TextSMSDeliveryFormProps = InjectedIntlProps & {
  eventType: string;
  eventCountry?: string;
  isFullNameRequired?: boolean;
};

const TextSMSDeliveryForm = (props: TextSMSDeliveryFormProps) => {
  const { intl } = props;

  const checkoutContext = useCheckoutContext();
  const [phoneValue, setPhoneValue] = useState<PhoneNumber | undefined>(undefined);
  const [emailError, setEmailError] = useState<string | undefined>(undefined);
  const [fullNameError, setFullNameError] = useState<string | undefined>(undefined);
  const [phoneError, setPhoneError] = useState<string | undefined>(undefined);

  const fullNameLabel = props.isFullNameRequired
    ? `${intl.formatMessage(messages.fullNamePlaceholder)} (${intl.formatMessage(messages.required)})`
    : intl.formatMessage(messages.fullNamePlaceholder);

  // TODO: remove this when we get out redux-form
  const dispatch = useDispatch();

  function updateFormField(
    value: any,
    reduxFormField?: string,
    contextKey?: keyof typeof checkoutContext.deliveryFormFields,
  ) {
    if (!reduxFormField && !contextKey) {
      return;
    }

    if (contextKey) {
      checkoutContext.setDeliveryFormFields({
        ...checkoutContext.deliveryFormFields,
        [contextKey]: value,
      });
    }

    // TODO: remove this when we get out redux-form
    if (reduxFormField) {
      dispatch(change(CHECKOUT_FORM_TITLE, reduxFormField, value));
    }
  }

  function setEmail(value: string) {
    const isEmailValid = !validateEmail(value, false);

    updateFormField(value, 'email', 'email');

    setEmailError(isEmailValid ? undefined : intl.formatMessage(messages.emailNotValid));
  }

  function setFullName(value: string) {
    if (!value && props.isFullNameRequired) {
      setFullNameError(intl.formatMessage(messages.required));
      return;
    }

    updateFormField(value, 'fullName', 'fullName');
  }

  function setNote(value: string) {
    updateFormField(value, 'paymentNode', 'notes');
  }

  async function setPhone(value: PhoneNumber) {
    setPhoneValue(value);
    const parsedPhoneNumber = parsePhoneNumber(value.phone);

    if (parsedPhoneNumber === undefined) {
      return;
    }

    /**
     * Instead to use updateFormField to update the context twice
     * we are using setDeliveryFormFields directly to avoid
     * concurrent updates
     */
    checkoutContext.setDeliveryFormFields({
      ...checkoutContext.deliveryFormFields,
      phoneCountryCode: parsedPhoneNumber.countryCode.toString(),
      phoneNumber: parsedPhoneNumber.phoneNumber,
    });

    // TODO: remove this when we get out redux-form - this is only saving the phone number in store
    updateFormField(
      {
        id: value.countryIso.toUpperCase(),
        value: value.countryIso.toUpperCase(),
        label: `+${parsedPhoneNumber.countryCode}`,
        name: `+${parsedPhoneNumber.countryCode}`,
      },
      'phonePre',
    );
    updateFormField(parsedPhoneNumber.phoneNumber, 'phoneNumber');
  }

  function validatePhone() {
    if (!phoneValue || !phoneValue.phone || !phoneValue.countryIso) {
      setPhoneError(intl.formatMessage(messages.required));

      return;
    }

    const isPhoneValid = validatePhoneNumber(phoneValue.phone, phoneValue.countryIso as CountryCode);

    if (!isPhoneValid) {
      setPhoneError(
        phoneValue.countryIso === 'us'
          ? intl.formatMessage(messages.phoneNumberValidationError)
          : intl.formatMessage(messages.phoneNumberValidationErrorNonUS),
      );
      return;
    }

    setPhoneError(undefined);
  }

  useEffect(() => {
    const requiredFields = ['phoneNumber'];

    if (props.isFullNameRequired) {
      requiredFields.push('fullName');
    }

    const isValid = isDeliveryFormValid({
      requiredFields,
      formFields: checkoutContext.deliveryFormFields,
      errors: { email: emailError, fullName: fullNameError, phoneNumber: phoneError },
    });

    checkoutContext.setIsFormValid(isValid);
  }, [checkoutContext, emailError, fullNameError, phoneError, props.isFullNameRequired]);

  return (
    <div className="text-sms-delivery-form" data-testid="text-sms-delivery-form">
      <PhoneNumberField
        name="phone-number"
        data-testid="phone-number-field"
        label={`${intl.formatMessage(messages.phoneNumberPlaceholder)} (${intl.formatMessage(messages.required)})`}
        error={phoneError}
        onBlur={() => validatePhone()}
        onPhoneDataChange={(val: PhoneNumber) => {
          setPhone(val);
        }}
        placeholder=""
        selectACountryLabel=""
        initialCountryIso={props.eventCountry}
      />
      <Tm1TextField
        name="full-name"
        type="text"
        data-testid="full-name-field"
        label={fullNameLabel}
        onChange={(val: React.ChangeEvent<HTMLInputElement>) => {
          setFullName(val.target.value);
        }}
        value={checkoutContext.deliveryFormFields.fullName}
        inputSize="medium"
        required={props.isFullNameRequired}
        validateOnBlur
        error={fullNameError}
      />
      <Tm1TextField
        name="email"
        type="email"
        data-testid="email-field"
        label={`${intl.formatMessage(messages.emailPlaceholder)}`}
        onChange={(val: React.ChangeEvent<HTMLInputElement>) => setEmail(val.target.value)}
        value={checkoutContext.deliveryFormFields.email}
        inputSize="medium"
        validateOnBlur
        error={emailError}
      />
      <Tm1TextArea
        name="notes"
        data-testid="notes-field"
        label={intl.formatMessage(messages.notePlaceholder)}
        onChange={(val: React.ChangeEvent<HTMLInputElement>) => setNote(val.target.value)}
        value={checkoutContext.deliveryFormFields.notes}
        inputSize="regular"
        validateOnBlur
        resize="horizontal"
        showCharacterCount
        maxLength={props.eventType === ARCHTICS_EVENT_TYPE ? ARCHTICS_NOTE_LIMITATION : HOST_NOTE_LIMITATION}
        error={undefined}
      />
    </div>
  );
};

export default injectIntl(TextSMSDeliveryForm);
